import React, { useState } from "react";
import { Dialog as SheetPrimitive } from "radix-ui";

import { cn } from "../../lib/utils";
import { XIcon } from "lucide-react";

import {
	Sheet,
	SheetClose,
	SheetDescription,
	SheetTitle,
} from "../ui/sheet";
import { Button } from "../ui/button";
import DialogModal from "../dialog-modal/dialog-modal.component";

const SIDEMODAL_DEFAULT_WIDTH = 560;

interface StructuredSideModalProps {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	title: string;
	description?: string;
	children: React.ReactNode;
	footer?: React.ReactNode;
	/** Rendered in the header between the title and the close button. */
	actions?: React.ReactNode;
	showOverlay?: boolean;
	width?: number;
	/** When provided, body + footer are wrapped in a <form> so Enter-key submit works. */
	onSubmit?: React.FormEventHandler<HTMLFormElement>;
	// When true, a user-initiated dismiss (X button, Escape, outside click)
	// is intercepted with a discard-changes confirm instead of closing right
	// away. Programmatic closes (caller flips `open` to false directly, e.g.
	// after a successful save) never go through onOpenChange, so they're
	// unaffected regardless of this flag.
	isDirty?: boolean;
}

export default function StructuredSideModal({
	open,
	onOpenChange,
	title,
	description,
	children,
	footer,
	actions,
	showOverlay = true,
	width = SIDEMODAL_DEFAULT_WIDTH,
	onSubmit,
	isDirty = false,
}: StructuredSideModalProps) {
	const [confirmDiscardOpen, setConfirmDiscardOpen] = useState(false);

	const handleOpenChange = (nextOpen: boolean) => {
		if (!nextOpen && isDirty) {
			setConfirmDiscardOpen(true);
			return;
		}
		onOpenChange(nextOpen);
	};

	const handleDiscardConfirm = () => {
		setConfirmDiscardOpen(false);
		onOpenChange(false);
	};

	const bodyContent = (
		<>
			<div
				className="flex-1 overflow-y-auto overscroll-y-contain"
				onWheel={(e) => e.stopPropagation()}
			>
				{children}
			</div>
			{footer && (
				<div className="shrink-0 border-t border-border px-4 py-3">
					{footer}
				</div>
			)}
		</>
	);

	return (
		<>
			<Sheet open={open} onOpenChange={handleOpenChange}>
				<SheetPrimitive.Portal>
					{showOverlay && (
						<SheetPrimitive.Overlay
							className={cn(
								"fixed inset-0 z-50 bg-black/50",
								"data-[state=open]:animate-in data-[state=open]:fade-in-0",
								"data-[state=closed]:animate-out data-[state=closed]:fade-out-0",
								"duration-100",
							)}
						/>
					)}

					<SheetPrimitive.Content
						data-slot="sheet-content"
						style={{ maxWidth: width }}
						className={cn(
							"fixed inset-y-0 right-0 z-50 flex flex-col w-full",
							"bg-background border-l border-border shadow-lg",
							"transition duration-200 ease-in-out",
							"data-[state=open]:animate-in data-[state=open]:slide-in-from-right-10 data-[state=open]:fade-in-0",
							"data-[state=closed]:animate-out data-[state=closed]:slide-out-to-right-10 data-[state=closed]:fade-out-0",
						)}
						onInteractOutside={(event) => {
							// SweetAlert2 renders its popup in its own document.body
							// portal, outside this Dialog's content — Radix otherwise
							// treats clicking it (e.g. "Cancel") as an outside click
							// and dismisses this sidemodal along with the alert.
							// Same for the discard-confirm DialogModal below, also its
							// own document.body portal.
							const target = event.target as HTMLElement;
							if (
								target?.closest(".swal2-container") ||
								target?.closest('[data-slot="dialog-content"]')
							) {
								event.preventDefault();
							}
						}}
						onEscapeKeyDown={(event) => {
							// Same portal issue as onInteractOutside, but for the Escape
							// key: let SweetAlert2/the discard-confirm dialog handle their
							// own Escape dismissal without also closing this sidemodal.
							if (
								document.querySelector(".swal2-container") ||
								confirmDiscardOpen
							) {
								event.preventDefault();
							}
						}}
					>
						<div className="sticky top-0 z-10 shrink-0 bg-background border-b border-border flex items-center gap-3 px-4 py-3">
							<div className="w-4 bg-accent-foreground absolute top-0 bottom-3 left-4 rounded-b-sm" />
							<div className="pl-8 flex flex-col gap-0.5 flex-1 min-w-0">
								<SheetTitle className="font-bold">{title}</SheetTitle>
								{description && (
									<SheetDescription>{description}</SheetDescription>
								)}
							</div>
							{actions && (
								<div className="flex items-center gap-2 shrink-0">
									{actions}
								</div>
							)}
							<SheetClose asChild>
								<Button variant="ghost" size="icon-sm" className="cursor-pointer shrink-0">
									<XIcon />
									<span className="sr-only">Close</span>
								</Button>
							</SheetClose>
						</div>

						{onSubmit ? (
							<form onSubmit={onSubmit} className="flex flex-col flex-1 min-h-0">
								{bodyContent}
							</form>
						) : (
							<div className="flex flex-col flex-1 min-h-0">
								{bodyContent}
							</div>
						)}
					</SheetPrimitive.Content>
				</SheetPrimitive.Portal>
			</Sheet>
			<DialogModal
				open={confirmDiscardOpen}
				onOpenChange={setConfirmDiscardOpen}
				title="Discard unsaved changes?"
				description="You have unsaved changes that will be lost."
				footer={
					<>
						<Button variant="outline" onClick={() => setConfirmDiscardOpen(false)}>
							Cancel
						</Button>
						<Button variant="destructive" onClick={handleDiscardConfirm}>
							Discard
						</Button>
					</>
				}
			/>
		</>
	);
}
